/**
 * StatusMessageService - Zentraler Service für Spielnachrichten
 *
 * Analysiert den Spielkontext und gibt passende Messages zurück.
 * Unterstützt Prioritäten, Templates und Platzhalter.
 */

import { getMessage, formatCards, formatPlayType } from './utils';
import * as templates from './templates';

// Prioritäten für Message-Auswahl
export const PRIORITY = {
  CRITICAL: 100,    // Ungültiger Zug, Fehler
  REACTION: 80,     // Sofortige Reaktionen auf Züge
  EFFECT: 70,       // Joker/Siegel Effekte
  ACTION: 60,       // Eigene Aktionen
  GAME_STATE: 50,   // Spielzustand (dran, warten)
  INFO: 30,         // Allgemeine Info
};

// Situation-Typen
export const SITUATION = {
  // Aktiver Spieler
  FIRST_TURN: 'FIRST_TURN',
  ROUND_STARTER: 'ROUND_STARTER',
  CAN_PLAY: 'CAN_PLAY',
  MUST_DRAW: 'MUST_DRAW',
  MUST_PASS: 'MUST_PASS',
  HAS_SHIELD: 'HAS_SHIELD',
  ALMOST_DONE: 'ALMOST_DONE',
  OPPONENT_WARNING: 'OPPONENT_WARNING',
  CANT_PASS_STARTER: 'CANT_PASS_STARTER',

  // Wartender Spieler
  WAITING_GENERIC: 'WAITING_GENERIC',
  WAITING_NEXT: 'WAITING_NEXT',
  WAITING_SPECTATING: 'WAITING_SPECTATING',

  // Reaktionen
  REACTION_PLAY: 'REACTION_PLAY',
  REACTION_DRAW: 'REACTION_DRAW',
  REACTION_PASS: 'REACTION_PASS',
  REACTION_FINISH: 'REACTION_FINISH',
  REACTION_BOMB: 'REACTION_BOMB',
  REACTION_FLUSH: 'REACTION_FLUSH',
  REACTION_STRAIGHT: 'REACTION_STRAIGHT',
  REACTION_RESET: 'REACTION_RESET',
  REACTION_SHIELD: 'REACTION_SHIELD',
  REACTION_BLOCKED: 'REACTION_BLOCKED',

  // Exchange
  EXCHANGE_SELECT_COUNT: 'EXCHANGE_SELECT_COUNT',
  EXCHANGE_WAIT_COUNT: 'EXCHANGE_WAIT_COUNT',
  EXCHANGE_PRESIDENT_GIVE: 'EXCHANGE_PRESIDENT_GIVE',
  EXCHANGE_VICE_GIVE: 'EXCHANGE_VICE_GIVE',
  EXCHANGE_LOSER_GIVE: 'EXCHANGE_LOSER_GIVE',
  EXCHANGE_VICE_LOSER_GIVE: 'EXCHANGE_VICE_LOSER_GIVE',
  EXCHANGE_WAIT_GIVE: 'EXCHANGE_WAIT_GIVE',
  EXCHANGE_LOSER_WAIT: 'EXCHANGE_LOSER_WAIT',
  EXCHANGE_RECEIVE: 'EXCHANGE_RECEIVE',
  EXCHANGE_COMPLETE: 'EXCHANGE_COMPLETE',

  // Seal Distribution
  SEAL_HAS_SEALS_NO_SELECTION: 'SEAL_HAS_SEALS_NO_SELECTION',
  SEAL_SELECTED: 'SEAL_SELECTED',
  SEAL_CARD_SELECTED: 'SEAL_CARD_SELECTED',
  SEAL_ALL_PLACED: 'SEAL_ALL_PLACED',
  SEAL_NO_SEALS: 'SEAL_NO_SEALS',
  SEAL_WAITING_ONE: 'SEAL_WAITING_ONE',
  SEAL_WAITING_MULTIPLE: 'SEAL_WAITING_MULTIPLE',

  // Shop
  SHOP_MY_TURN: 'SHOP_MY_TURN',
  SHOP_WAITING: 'SHOP_WAITING',
  SHOP_BOUGHT_JOKER: 'SHOP_BOUGHT_JOKER',
  SHOP_BOUGHT_SEAL: 'SHOP_BOUGHT_SEAL',
  SHOP_BOUGHT_BUNDLE: 'SHOP_BOUGHT_BUNDLE',
  SHOP_SOLD_JOKER: 'SHOP_SOLD_JOKER',
  SHOP_REROLLED: 'SHOP_REROLLED',
  SHOP_CANT_AFFORD: 'SHOP_CANT_AFFORD',
  SHOP_DONE: 'SHOP_DONE',

  // Game End
  GAME_END_WINNER: 'GAME_END_WINNER',
  GAME_END_SECOND: 'GAME_END_SECOND',
  GAME_END_MIDDLE: 'GAME_END_MIDDLE',
  GAME_END_SHITHEAD: 'GAME_END_SHITHEAD',

  // Effekte
  JOKER_TRIGGERED: 'JOKER_TRIGGERED',
  SEAL_TRIGGERED: 'SEAL_TRIGGERED',

  // Fehler
  INVALID_PLAY: 'INVALID_PLAY',
};

class StatusMessageService {
  constructor() {
    this.lastSituation = null;
    this.lastMessage = '';
  }

  /**
   * Hauptmethode: Analysiert Kontext und gibt Message zurück
   * @param {Object} context - Spielkontext
   * @returns {{ text: string, situation: string, priority: number }}
   */
  getMessage(context) {
    const situation = this.analyzeSituation(context);
    const messageData = this.buildMessageData(context);
    const text = this.selectMessage(situation, messageData);

    this.lastSituation = situation;
    this.lastMessage = text;

    return {
      text,
      situation: situation.type,
      priority: situation.priority,
    };
  }

  /**
   * Analysiert den Spielkontext und bestimmt die Situation
   */
  analyzeSituation(context) {
    const {
      phase,
      isMyTurn,
      wasInvalidPlay,
      jokerTriggered,
      sealTriggered,
      lastAction,
    } = context;

    // Höchste Priorität: Fehler und Effekte
    if (wasInvalidPlay) {
      return { type: SITUATION.INVALID_PLAY, priority: PRIORITY.CRITICAL };
    }

    if (jokerTriggered) {
      return { type: SITUATION.JOKER_TRIGGERED, priority: PRIORITY.EFFECT, data: jokerTriggered };
    }

    if (sealTriggered) {
      return { type: SITUATION.SEAL_TRIGGERED, priority: PRIORITY.EFFECT, data: sealTriggered };
    }

    // Phase-spezifische Analyse
    if (phase === 'sealDistribution') {
      return this.analyzeSealDistribution(context);
    }

    if (phase === 'exchanging') {
      return this.analyzeExchange(context);
    }

    if (phase === 'shop') {
      return this.analyzeShop(context);
    }

    if (phase === 'gameEnd' || context.situation === 'game_end') {
      return this.analyzeGameEnd(context);
    }

    // Spielphase
    if (lastAction && !isMyTurn) {
      return this.analyzeReaction(context);
    }

    if (isMyTurn) {
      return this.analyzeMyTurn(context);
    }

    return this.analyzeWaiting(context);
  }

  /**
   * Analysiert: Ich bin dran
   */
  analyzeMyTurn(context) {
    const {
      isFirstTurn,
      isRoundStarter,
      hasValidPlays,
      canDraw,
      myHandCount,
      shieldUses,
      opponentAlmostDone,
      opponentWarningName,
    } = context;

    // Erste Karte der Runde
    if (isFirstTurn) {
      return { type: SITUATION.FIRST_TURN, priority: PRIORITY.GAME_STATE };
    }

    // Runden-Starter (hat Stich gewonnen)
    if (isRoundStarter && !hasValidPlays) {
      return { type: SITUATION.CANT_PASS_STARTER, priority: PRIORITY.GAME_STATE };
    }

    if (isRoundStarter) {
      return { type: SITUATION.ROUND_STARTER, priority: PRIORITY.GAME_STATE };
    }

    // Gegner fast fertig - Warnung
    if (opponentAlmostDone) {
      return {
        type: SITUATION.OPPONENT_WARNING,
        priority: PRIORITY.GAME_STATE,
        data: { name: opponentWarningName }
      };
    }

    // Keine gültigen Züge
    if (!hasValidPlays) {
      if (canDraw) {
        return { type: SITUATION.MUST_DRAW, priority: PRIORITY.GAME_STATE };
      }
      return { type: SITUATION.MUST_PASS, priority: PRIORITY.GAME_STATE };
    }

    // Hat Schild
    if (shieldUses > 0 && !hasValidPlays) {
      return { type: SITUATION.HAS_SHIELD, priority: PRIORITY.GAME_STATE };
    }

    // Fast fertig
    if (myHandCount > 0 && myHandCount <= 2) {
      return { type: SITUATION.ALMOST_DONE, priority: PRIORITY.GAME_STATE };
    }

    // Generisch
    return { type: SITUATION.CAN_PLAY, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Analysiert: Anderer Spieler ist dran
   */
  analyzeWaiting(context) {
    const { amINext, myHandCount, myPosition } = context;

    // Ich bin raus, schaue zu
    if (myHandCount === 0 && myPosition !== undefined) {
      return { type: SITUATION.WAITING_SPECTATING, priority: PRIORITY.GAME_STATE };
    }

    // Ich bin als Nächstes dran
    if (amINext) {
      return { type: SITUATION.WAITING_NEXT, priority: PRIORITY.GAME_STATE };
    }

    return { type: SITUATION.WAITING_GENERIC, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Analysiert: Reaktion auf Aktion
   */
  analyzeReaction(context) {
    const { lastAction, lastPlaySize, lastPlayType, wasBlocked } = context;

    if (wasBlocked) {
      return { type: SITUATION.REACTION_BLOCKED, priority: PRIORITY.REACTION };
    }

    switch (lastAction) {
      case 'play':
        // Spezial-Kombinationen
        if (lastPlaySize >= 4) {
          return { type: SITUATION.REACTION_BOMB, priority: PRIORITY.REACTION };
        }
        if (lastPlayType === 'flush') {
          return { type: SITUATION.REACTION_FLUSH, priority: PRIORITY.REACTION };
        }
        if (lastPlayType === 'straight' || lastPlayType === 'straightFlush') {
          return { type: SITUATION.REACTION_STRAIGHT, priority: PRIORITY.REACTION };
        }
        return { type: SITUATION.REACTION_PLAY, priority: PRIORITY.REACTION };

      case 'draw':
        return { type: SITUATION.REACTION_DRAW, priority: PRIORITY.REACTION };

      case 'pass':
        return { type: SITUATION.REACTION_PASS, priority: PRIORITY.REACTION };

      case 'shield':
        return { type: SITUATION.REACTION_SHIELD, priority: PRIORITY.REACTION };

      case 'finish':
        return { type: SITUATION.REACTION_FINISH, priority: PRIORITY.REACTION };

      case 'reset':
        return { type: SITUATION.REACTION_RESET, priority: PRIORITY.REACTION };

      default:
        return { type: SITUATION.WAITING_GENERIC, priority: PRIORITY.GAME_STATE };
    }
  }

  /**
   * Analysiert: Kartentausch-Phase
   */
  analyzeExchange(context) {
    const { exchangeSubPhase, myRole, isMyExchangeTurn, isCardCountSelector } = context;

    // Kartenanzahl wählen
    if (exchangeSubPhase === 'cardCount') {
      if (isCardCountSelector) {
        return { type: SITUATION.EXCHANGE_SELECT_COUNT, priority: PRIORITY.GAME_STATE };
      }
      return { type: SITUATION.EXCHANGE_WAIT_COUNT, priority: PRIORITY.GAME_STATE };
    }

    // Karten geben
    if (exchangeSubPhase === 'giving' || isMyExchangeTurn) {
      if (!isMyExchangeTurn) {
        return { type: SITUATION.EXCHANGE_WAIT_GIVE, priority: PRIORITY.GAME_STATE };
      }

      switch (myRole) {
        case 'president':
          return { type: SITUATION.EXCHANGE_PRESIDENT_GIVE, priority: PRIORITY.GAME_STATE };
        case 'vicePresident':
          return { type: SITUATION.EXCHANGE_VICE_GIVE, priority: PRIORITY.GAME_STATE };
        case 'loser':
          return { type: SITUATION.EXCHANGE_LOSER_GIVE, priority: PRIORITY.GAME_STATE };
        case 'viceLoser':
          return { type: SITUATION.EXCHANGE_VICE_LOSER_GIVE, priority: PRIORITY.GAME_STATE };
        default:
          return { type: SITUATION.EXCHANGE_WAIT_GIVE, priority: PRIORITY.GAME_STATE };
      }
    }

    // Receiving-Phase: Presidents/Vice geben Karten zurück, Loser wartet auf Karten
    if (exchangeSubPhase === 'receiving') {
      if (myRole === 'president') {
        return { type: SITUATION.EXCHANGE_PRESIDENT_GIVE, priority: PRIORITY.GAME_STATE };
      }
      if (myRole === 'vicePresident') {
        return { type: SITUATION.EXCHANGE_VICE_GIVE, priority: PRIORITY.GAME_STATE };
      }
      if (myRole === 'loser' || myRole === 'viceLoser') {
        return { type: SITUATION.EXCHANGE_LOSER_WAIT, priority: PRIORITY.GAME_STATE };
      }
      return { type: SITUATION.EXCHANGE_WAIT_GIVE, priority: PRIORITY.GAME_STATE };
    }

    // Fertig
    if (exchangeSubPhase === 'complete') {
      return { type: SITUATION.EXCHANGE_COMPLETE, priority: PRIORITY.GAME_STATE };
    }

    return { type: SITUATION.EXCHANGE_WAIT_GIVE, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Analysiert: Siegel-Verteilungs-Phase
   */
  analyzeSealDistribution(context) {
    const {
      hasSealsToPlace,
      selectedSealId,
      selectedCardCount,
      allSealsPlaced,
      waitingCount,
      isPlayerReady,
    } = context;

    // Spieler hat keine Siegel
    if (!hasSealsToPlace) {
      return { type: SITUATION.SEAL_NO_SEALS, priority: PRIORITY.GAME_STATE };
    }

    // Spieler ist schon fertig - warte auf andere
    if (isPlayerReady) {
      if (waitingCount === 1) {
        return { type: SITUATION.SEAL_WAITING_ONE, priority: PRIORITY.GAME_STATE };
      }
      if (waitingCount > 1) {
        return { type: SITUATION.SEAL_WAITING_MULTIPLE, priority: PRIORITY.GAME_STATE, data: { count: waitingCount } };
      }
    }

    // Alle Siegel platziert, noch nicht bestätigt
    if (allSealsPlaced) {
      return { type: SITUATION.SEAL_ALL_PLACED, priority: PRIORITY.GAME_STATE };
    }

    // Siegel ausgewählt - warte auf Karten-Tap
    if (selectedSealId) {
      return { type: SITUATION.SEAL_SELECTED, priority: PRIORITY.GAME_STATE };
    }

    // Karte ausgewählt (card-first flow) - warte auf Siegel-Tap
    if (selectedCardCount > 0) {
      return { type: SITUATION.SEAL_CARD_SELECTED, priority: PRIORITY.GAME_STATE };
    }

    // Noch Siegel zu platzieren
    return { type: SITUATION.SEAL_HAS_SEALS_NO_SELECTION, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Analysiert: Shop-Phase
   */
  analyzeShop(context) {
    const { isMyShopTurn, shopAction, shopActionSuccess } = context;

    // Reaktion auf Shop-Aktion
    if (shopAction) {
      if (!shopActionSuccess) {
        return { type: SITUATION.SHOP_CANT_AFFORD, priority: PRIORITY.ACTION };
      }

      switch (shopAction) {
        case 'buyJoker':
          return { type: SITUATION.SHOP_BOUGHT_JOKER, priority: PRIORITY.ACTION };
        case 'buySeal':
          return { type: SITUATION.SHOP_BOUGHT_SEAL, priority: PRIORITY.ACTION };
        case 'buyBundle':
          return { type: SITUATION.SHOP_BOUGHT_BUNDLE, priority: PRIORITY.ACTION };
        case 'sellJoker':
          return { type: SITUATION.SHOP_SOLD_JOKER, priority: PRIORITY.ACTION };
        case 'reroll':
          return { type: SITUATION.SHOP_REROLLED, priority: PRIORITY.ACTION };
        case 'done':
          return { type: SITUATION.SHOP_DONE, priority: PRIORITY.ACTION };
        default:
          break;
      }
    }

    if (isMyShopTurn) {
      return { type: SITUATION.SHOP_MY_TURN, priority: PRIORITY.GAME_STATE };
    }

    return { type: SITUATION.SHOP_WAITING, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Analysiert: Spielende
   */
  analyzeGameEnd(context) {
    const { myPosition, totalPlayers } = context;

    if (myPosition === 1) {
      return { type: SITUATION.GAME_END_WINNER, priority: PRIORITY.GAME_STATE };
    }

    if (myPosition === 2) {
      return { type: SITUATION.GAME_END_SECOND, priority: PRIORITY.GAME_STATE };
    }

    if (myPosition === totalPlayers || myPosition === -1) {
      return { type: SITUATION.GAME_END_SHITHEAD, priority: PRIORITY.GAME_STATE };
    }

    return { type: SITUATION.GAME_END_MIDDLE, priority: PRIORITY.GAME_STATE };
  }

  /**
   * Baut Message-Daten aus Kontext
   */
  buildMessageData(context) {
    const {
      activePlayerName,
      nextPlayerName,
      myHandCount,
      roundNumber,
      points,
      myPosition,
      lastPlayedCards,
      lastPlayType,
      lastPlaySize,
      exchangeCount,
      jokerName,
      jokerDescription,
      sealName,
      sealEffect,
      shopItemName,
      shopPrice,
    } = context;

    return {
      name: activePlayerName || 'Spieler',
      nextName: nextPlayerName || 'Jemand',
      count: myHandCount || exchangeCount || 1,
      round: roundNumber || 1,
      points: points || 0,
      position: myPosition || 0,
      cards: lastPlayedCards ? formatCards(lastPlayedCards) : '',
      playType: lastPlayType ? formatPlayType(lastPlayType, lastPlaySize) : '',
      jokerName: jokerName || '',
      description: jokerDescription || '',
      sealName: sealName || '',
      effect: sealEffect || '',
      item: shopItemName || '',
      price: shopPrice || 0,
    };
  }

  /**
   * Wählt Message basierend auf Situation
   */
  selectMessage(situation, data) {
    const { type } = situation;

    // Situations-basierte Template-Auswahl
    switch (type) {
      // Aktiver Spieler
      case SITUATION.FIRST_TURN:
        return getMessage(templates.activePlayer.FIRST_TURN, data);
      case SITUATION.ROUND_STARTER:
        return getMessage(templates.activePlayer.ROUND_STARTER, data);
      case SITUATION.CAN_PLAY:
        return getMessage(templates.activePlayer.CAN_PLAY, data);
      case SITUATION.MUST_DRAW:
        return getMessage(templates.activePlayer.MUST_DRAW, data);
      case SITUATION.MUST_PASS:
        return getMessage(templates.activePlayer.MUST_PASS, data);
      case SITUATION.HAS_SHIELD:
        return getMessage(templates.activePlayer.HAS_SHIELD, data);
      case SITUATION.ALMOST_DONE:
        return getMessage(templates.activePlayer.ALMOST_DONE, data);
      case SITUATION.OPPONENT_WARNING:
        return getMessage(templates.activePlayer.OPPONENT_WARNING, { ...data, name: situation.data?.name || data.name });
      case SITUATION.CANT_PASS_STARTER:
        return getMessage(templates.activePlayer.CANT_PASS_STARTER, data);

      // Wartender Spieler
      case SITUATION.WAITING_GENERIC:
        return getMessage(templates.waitingPlayer.GENERIC, data);
      case SITUATION.WAITING_NEXT:
        return getMessage(templates.waitingPlayer.NEXT_UP, data);
      case SITUATION.WAITING_SPECTATING:
        return getMessage(templates.waitingPlayer.SPECTATING, data);

      // Reaktionen
      case SITUATION.REACTION_PLAY:
        return getMessage(templates.waitingPlayer.PLAYER_PLAYED, data);
      case SITUATION.REACTION_DRAW:
        return getMessage(templates.waitingPlayer.PLAYER_DREW, data);
      case SITUATION.REACTION_PASS:
        return getMessage(templates.waitingPlayer.PLAYER_PASSED, data);
      case SITUATION.REACTION_FINISH:
        return getMessage(templates.waitingPlayer.PLAYER_FINISHED, data);
      case SITUATION.REACTION_BOMB:
        return getMessage(templates.waitingPlayer.PLAYER_BOMBED, data);
      case SITUATION.REACTION_FLUSH:
        return getMessage(templates.waitingPlayer.PLAYER_FLUSHED, data);
      case SITUATION.REACTION_STRAIGHT:
        return getMessage(templates.waitingPlayer.PLAYER_STRAIGHTED, data);
      case SITUATION.REACTION_RESET:
        return getMessage(templates.waitingPlayer.PLAYER_RESET, data);
      case SITUATION.REACTION_SHIELD:
        return getMessage(templates.waitingPlayer.PLAYER_USED_SHIELD, data);
      case SITUATION.REACTION_BLOCKED:
        return getMessage(templates.waitingPlayer.BLOCKED_ME, data);

      // Exchange
      case SITUATION.EXCHANGE_SELECT_COUNT:
        return getMessage(templates.exchange.SELECT_COUNT, data);
      case SITUATION.EXCHANGE_WAIT_COUNT:
        return getMessage(templates.exchange.WAIT_COUNT, data);
      case SITUATION.EXCHANGE_PRESIDENT_GIVE:
        return getMessage(templates.exchange.PRESIDENT_GIVE, data);
      case SITUATION.EXCHANGE_VICE_GIVE:
        return getMessage(templates.exchange.VICE_GIVE, data);
      case SITUATION.EXCHANGE_LOSER_GIVE:
        return getMessage(templates.exchange.LOSER_GIVE, data);
      case SITUATION.EXCHANGE_VICE_LOSER_GIVE:
        return getMessage(templates.exchange.VICE_LOSER_GIVE, data);
      case SITUATION.EXCHANGE_WAIT_GIVE:
        return getMessage(templates.exchange.WAIT_GIVE, data);
      case SITUATION.EXCHANGE_LOSER_WAIT:
        return getMessage(templates.exchange.LOSER_WAIT, data);
      case SITUATION.EXCHANGE_RECEIVE:
        return getMessage(templates.exchange.RECEIVE_CARDS, data);
      case SITUATION.EXCHANGE_COMPLETE:
        return getMessage(templates.exchange.COMPLETE, data);

      // Seal Distribution
      case SITUATION.SEAL_HAS_SEALS_NO_SELECTION:
        return getMessage(templates.sealDistribution.HAS_SEALS_NO_SELECTION, data);
      case SITUATION.SEAL_SELECTED:
        return getMessage(templates.sealDistribution.SEAL_SELECTED, data);
      case SITUATION.SEAL_CARD_SELECTED:
        return getMessage(templates.sealDistribution.CARD_SELECTED, data);
      case SITUATION.SEAL_ALL_PLACED:
        return getMessage(templates.sealDistribution.ALL_PLACED, data);
      case SITUATION.SEAL_NO_SEALS:
        return getMessage(templates.sealDistribution.NO_SEALS, data);
      case SITUATION.SEAL_WAITING_ONE:
        return getMessage(templates.sealDistribution.WAITING_ONE, data);
      case SITUATION.SEAL_WAITING_MULTIPLE:
        return getMessage(templates.sealDistribution.WAITING_MULTIPLE, { ...data, count: situation.data?.count || 0 });

      // Shop
      case SITUATION.SHOP_MY_TURN:
        return getMessage(templates.shop.MY_TURN, data);
      case SITUATION.SHOP_WAITING:
        return getMessage(templates.shop.WAITING, data);
      case SITUATION.SHOP_BOUGHT_JOKER:
        return getMessage(templates.shop.BOUGHT_JOKER, data);
      case SITUATION.SHOP_BOUGHT_SEAL:
        return getMessage(templates.shop.BOUGHT_SEAL, data);
      case SITUATION.SHOP_BOUGHT_BUNDLE:
        return getMessage(templates.shop.BOUGHT_BUNDLE, data);
      case SITUATION.SHOP_SOLD_JOKER:
        return getMessage(templates.shop.SOLD_JOKER, data);
      case SITUATION.SHOP_REROLLED:
        return getMessage(templates.shop.REROLLED, data);
      case SITUATION.SHOP_CANT_AFFORD:
        return getMessage(templates.shop.CANT_AFFORD, data);
      case SITUATION.SHOP_DONE:
        return getMessage(templates.shop.DONE, data);

      // Game End
      case SITUATION.GAME_END_WINNER:
        return getMessage(templates.gameEnd.WINNER, data) + ' ' + getMessage(templates.gameEnd.WINNER_POINTS, data);
      case SITUATION.GAME_END_SECOND:
        return getMessage(templates.gameEnd.SECOND_PLACE, data);
      case SITUATION.GAME_END_MIDDLE:
        return getMessage(templates.gameEnd.MIDDLE_PLACE, data);
      case SITUATION.GAME_END_SHITHEAD:
        return getMessage(templates.gameEnd.SHITHEAD, data) + ' ' + getMessage(templates.gameEnd.SHITHEAD_POINTS, data);

      // Effekte
      case SITUATION.JOKER_TRIGGERED:
        return getMessage(templates.reactions.JOKER_TRIGGERED, data);
      case SITUATION.SEAL_TRIGGERED:
        return getMessage(templates.reactions.SEAL_TRIGGERED, data);

      // Fehler
      case SITUATION.INVALID_PLAY:
        return getMessage(templates.reactions.INVALID_PLAY, data);

      default:
        return getMessage(templates.activePlayer.GENERIC, data);
    }
  }

  /**
   * Gibt eine direkte Reaktions-Message zurück (für Quick-Feedback)
   */
  getReaction(reactionType, data = {}) {
    switch (reactionType) {
      case 'good':
        return getMessage(templates.reactions.GOOD_PLAY, data);
      case 'boring':
        return getMessage(templates.reactions.BORING_PLAY, data);
      case 'bomb':
        return getMessage(templates.reactions.BOMB_PLAYED, data);
      case 'flush':
        return getMessage(templates.reactions.FLUSH_PLAYED, data);
      case 'straight':
        return getMessage(templates.reactions.STRAIGHT_PLAYED, data);
      case 'reset':
        return getMessage(templates.reactions.RESET_PLAYED, data);
      case 'finish':
        return getMessage(templates.reactions.JUST_FINISHED, data);
      case 'invalid':
        return getMessage(templates.reactions.INVALID_PLAY, data);
      case 'coins':
        return getMessage(templates.reactions.COINS_GAINED, data);
      case 'points':
        return getMessage(templates.reactions.POINTS_GAINED, data);
      case 'mult':
        return getMessage(templates.reactions.MULT_INCREASED, data);
      default:
        return '';
    }
  }

  /**
   * Gibt Selection-Feedback zurück basierend auf ausgewählten Karten
   * @param {Object} context - Selection-Kontext
   * @returns {string|null} - Message oder null wenn kein Feedback nötig
   */
  getSelectionFeedback(context) {
    const {
      selectedCards,
      isSelectionValid,
      hasValidPlays,
      lastPlaySize,
      playType,
    } = context;

    // Keine Auswahl - nur wenn Spieler keine Optionen hat
    if (!selectedCards || selectedCards.length === 0) {
      if (!hasValidPlays) {
        return getMessage(templates.selection.NO_SELECTION_MUST_DRAW, {});
      }
      return null; // Keine Message wenn Optionen vorhanden aber nichts ausgewählt
    }

    const selectedCount = selectedCards.length;

    // Gültige Auswahl
    if (isSelectionValid) {
      // Spezielle Kombis - prüfe playType, nicht nur Anzahl!
      if (playType === 'pokerWithKicker') {
        return getMessage(templates.selection.QUAD_SELECTED, {});
      }
      if (playType === 'straightFlush' || playType === 'royalFlush') {
        return getMessage(templates.selection.STRAIGHT_FLUSH_SELECTED, {});
      }
      if (playType === 'flush') {
        return getMessage(templates.selection.FLUSH_SELECTED, {});
      }
      if (playType === 'straight') {
        return getMessage(templates.selection.STRAIGHT_SELECTED, {});
      }
      if (playType === 'fullHouse') {
        return getMessage(templates.selection.FULL_HOUSE_SELECTED, {});
      }
      if (playType === 'twoPairs') {
        return getMessage(templates.selection.TWO_PAIR_SELECTED, {});
      }
      if (playType === 'triple') {
        return getMessage(templates.selection.TRIPLE_SELECTED, {});
      }
      if (playType === 'pair') {
        return getMessage(templates.selection.PAIR_SELECTED, {});
      }
      // Standard gültige Auswahl (single oder unbekannt)
      return getMessage(templates.selection.VALID_SELECTION, {});
    }

    // Ungültige Auswahl
    if (lastPlaySize > 0 && selectedCount !== lastPlaySize) {
      return getMessage(templates.selection.WRONG_COUNT, {
        count: lastPlaySize,
        selected: selectedCount
      });
    }

    // Allgemein ungültig
    return getMessage(templates.selection.INVALID_COMBO, {});
  }
}

// Singleton-Export
export const statusMessageService = new StatusMessageService();
export default statusMessageService;
